import { EffectDef, makeGain } from './types';

/** Convolution reverb with a procedurally generated decay-noise impulse. */
export const reverb: EffectDef = {
  type: 'reverb',
  name: 'Reverb',
  params: [
    { key: 'decay', label: 'Decay', min: 0.2, max: 8, default: 2.5, step: 0.1, unit: 's' },
    { key: 'preDelay', label: 'Pre', min: 0, max: 0.2, default: 0.02, step: 0.005, unit: 's' },
    { key: 'wet', label: 'Wet', min: 0, max: 1, default: 0.3, step: 0.01 },
  ],
  build(ctx, p) {
    const input = makeGain(ctx, 1);
    const output = makeGain(ctx, 1);
    const dry = makeGain(ctx, 1 - p.wet);
    const wetIn = makeGain(ctx, 1);
    const convolver = ctx.createConvolver();
    const wetOut = makeGain(ctx, p.wet);

    input.connect(dry);
    dry.connect(output);
    input.connect(wetIn);
    wetIn.connect(convolver);
    convolver.connect(wetOut);
    wetOut.connect(output);

    const apply = (params: Record<string, number>) => {
      dry.gain.value = 1 - params.wet;
      wetOut.gain.value = params.wet;
      convolver.buffer = makeImpulse(ctx, params.decay, params.preDelay);
    };
    apply(p);

    return {
      input,
      output,
      update: apply,
      dispose: () => {
        convolver.disconnect();
        dry.disconnect();
        wetIn.disconnect();
        wetOut.disconnect();
        input.disconnect();
        output.disconnect();
      },
    };
  },
};

// Cache impulses by (decay, preDelay) so we don't regenerate on every update.
const impulseCache = new Map<string, AudioBuffer>();

function makeImpulse(ctx: BaseAudioContext, decay: number, preDelay: number): AudioBuffer {
  // Quantize so knob drags reuse cached impulses.
  const qDecay = Math.round(decay * 10) / 10;
  const qPre = Math.round(preDelay * 100) / 100;
  const key = `${qDecay}:${qPre}`;
  const cached = impulseCache.get(key);
  if (cached) return cached;

  const rate = ctx.sampleRate;
  const preLen = Math.floor(rate * qPre);
  const tailLen = Math.max(rate * 0.05, Math.floor(rate * qDecay));
  const length = preLen + tailLen;
  const buffer = ctx.createBuffer(2, length, rate);

  for (let ch = 0; ch < 2; ch++) {
    const data = buffer.getChannelData(ch);
    for (let i = preLen; i < length; i++) {
      const t = (i - preLen) / tailLen;
      const env = Math.pow(1 - t, 2.8);
      data[i] = (Math.random() * 2 - 1) * env;
    }
  }

  if (impulseCache.size > 24) impulseCache.clear();
  impulseCache.set(key, buffer);
  return buffer;
}